import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import Header from "../components/Header";
import BottomNav from "../components/BottomNav";
import HeatMeter from "../components/HeatMeter";
import NewsTicker from "../components/NewsTicker";
import ContestCard from "../components/ContestCard";
import TrendCard from "../components/TrendCard";
import QuickTour from "../components/QuickTour";
import Avatar from "../components/Avatar";
import DailyCheckin from "../components/DailyCheckin";
import { Flame, TrendingUp, ArrowRight, HelpCircle, Sparkles, Gem, Trophy, Swords } from "lucide-react";
import { getContests, getTrendIndexes, getFeed, getStatus, tickMission } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";

export default function Dashboard() {
  const { user, setUser } = useAuth();
  const [contests, setContests] = useState([]);
  const [indexes, setIndexes] = useState([]);
  const [feed, setFeed] = useState([]);
  const [status, setStatus] = useState(null);
  const [tourOpen, setTourOpen] = useState(false);

  const loadStatus = () =>
    getStatus().then((s) => {
      setStatus(s);
      if (s.user) setUser(s.user);
    }).catch(() => {});

  useEffect(() => {
    loadStatus();
    Promise.all([getContests(), getTrendIndexes({ sort: "heat" }), getFeed()])
      .then(([c, i, f]) => { setContests(c); setIndexes(i); setFeed(f); })
      .catch(() => {});
    try { if (!localStorage.getItem("fanpuff_tour_seen")) setTourOpen(true); } catch (e) { console.debug("[dashboard] tour-seen unavailable", e); }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const hot = indexes.slice(0, 5);
  const featured = contests.filter(c => c.featured).slice(0, 3);
  const overallHeat = Math.round(
    indexes.slice(0, 20).reduce((acc, x) => acc + (x.heat_score || 0), 0) / Math.max(1, Math.min(20, indexes.length))
  ) || 72;

  const tier = status?.tier;
  const xp = status?.user?.xp ?? 0;
  const nextMs = tier?.next;

  return (
    <div className="min-h-screen pb-safe" data-testid="dashboard-page">
      <Header title="Fanpuff" />
      <div className="max-w-xl mx-auto px-4 pt-4 space-y-5">
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} className="flex items-end justify-between">
          <div>
            <p className="text-xs uppercase tracking-widest text-white/40">Welcome back</p>
            <h1 className="font-display text-3xl font-bold flex items-center gap-2">
              Hey {user?.name?.split(" ")[0] || "Fan"}
            </h1>
          </div>
          <button onClick={() => setTourOpen(true)} data-testid="dashboard-help"
            className="chip hover:bg-white/10"
            style={{ background: "rgba(255,255,255,0.05)", color: "#fff", border: "1px solid rgba(255,255,255,0.1)" }}>
            <HelpCircle className="w-3 h-3" /> How it works
          </button>
        </motion.div>

        {/* Credits wallet + Next Milestone */}
        <section className="grid grid-cols-2 gap-3">
          <div className="glass-strong rounded-2xl p-4 relative overflow-hidden" data-testid="credits-card">
            <div aria-hidden className="pointer-events-none absolute -top-6 -right-6 w-28 h-28 rounded-full opacity-30" style={{ background: "radial-gradient(circle,#00FF66,transparent 70%)" }} />
            <p className="text-[10px] uppercase tracking-widest font-bold text-white/50">Fanpuff Credits</p>
            <p className="font-display text-2xl font-bold mt-1 neon-up font-mono-brand">{(user?.fan_credits || 0).toLocaleString("en-IN")}</p>
            {(() => {
              const growth = (user?.fan_credits || 0) - 100000;
              const positive = growth >= 0;
              return (
                <p className="text-[10px] mt-1" style={{ color: positive ? "#00FF66" : "#FF0055" }}>
                  {positive ? "+" : ""}{growth.toLocaleString("en-IN")} from start
                </p>
              );
            })()}
          </div>
          <Link to="/vault" className="glass-strong rounded-2xl p-4 relative overflow-hidden block" data-testid="next-milestone-card">
            <div aria-hidden className="pointer-events-none absolute -top-6 -right-6 w-28 h-28 rounded-full opacity-30" style={{ background: "radial-gradient(circle,#FFD600,transparent 70%)" }} />
            <p className="text-[10px] uppercase tracking-widest font-bold text-white/50">Next Reward</p>
            {nextMs ? (
              <>
                <p className="font-display text-base font-bold mt-1 leading-tight truncate">{nextMs.name}</p>
                <p className="text-[10px] text-white/50 mt-0.5 font-mono-brand">at {nextMs.credits.toLocaleString("en-IN")} credits</p>
                <div className="mt-2 h-1.5 rounded-full bg-white/8 overflow-hidden">
                  <div className="h-full" style={{ width: `${Math.min(100, (tier?.progress || 0) * 100).toFixed(0)}%`, background: "linear-gradient(90deg,#FFD600,#FF0055,#B026FF)" }} />
                </div>
              </>
            ) : (
              <p className="text-[10px] text-[#FFD600] mt-1">Max milestone reached.</p>
            )}
          </Link>
        </section>

        {/* Daily check-in + missions */}
        <DailyCheckin status={status} onRefresh={loadStatus} />

        <HeatMeter value={overallHeat} label="Culture Form" />

        <NewsTicker />

        {/* Quick tiles */}
        <section className="grid grid-cols-4 gap-2">
          <QuickTile icon={Sparkles} color="#B026FF" label="Circle" to="/contests" tid="quick-build" />
          <QuickTile icon={Flame} color="#FF0055" label="Top Form" to="/heat" tid="quick-heat"
            onClick={() => tickMission("view_market").catch(() => {})} />
          <QuickTile icon={Gem} color="#FFD600" label="Vault" to="/vault" tid="quick-vault" />
          <QuickTile icon={Trophy} color="#00F0FF" label="Ranks" to="/leaderboard" tid="quick-ranks" />
        </section>

        <Link to="/requests" data-testid="dash-request-shortcut" className="block glass rounded-2xl p-4 card-hover">
          <p className="text-xs uppercase tracking-widest font-bold text-white/50">Missing someone?</p>
          <p className="font-display font-bold mt-1">Request an influencer →</p>
        </Link>

        <Link to="/duel/new" data-testid="dash-duel-cta" className="block glass-strong rounded-2xl p-4 relative overflow-hidden card-hover">
          <div aria-hidden className="pointer-events-none absolute -top-10 -right-10 w-40 h-40 rounded-full opacity-40" style={{ background: "radial-gradient(circle,#FF0055,transparent 70%)" }} />
          <div className="flex items-center gap-3 relative z-10">
            <span className="w-11 h-11 rounded-2xl grid place-items-center shrink-0" style={{ background: "linear-gradient(135deg,#FF005566,#B026FF66)", border: "1px solid #FF005555" }}>
              <Swords className="w-5 h-5 text-white" />
            </span>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#FF0055]">New · 1v1</p>
              <p className="font-display text-lg font-bold leading-tight">Duel a friend — winner takes credits</p>
              <p className="text-[11px] text-white/60">Pick 6, share the link, beat them instantly.</p>
            </div>
            <ArrowRight className="w-4 h-4 text-white/60" />
          </div>
        </Link>

        <section>
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-display text-xl font-bold">Featured Contests</h2>
            <Link to="/contests" className="text-xs tracking-widest uppercase text-white/60 hover:text-white inline-flex items-center gap-1" data-testid="see-all-contests">
              See all <ArrowRight className="w-3 h-3" />
            </Link>
          </div>
          <div className="grid grid-cols-1 gap-4">
            {featured.map((c) => <ContestCard key={c.contest_id} contest={c} />)}
          </div>
        </section>

        <section>
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-display text-xl font-bold flex items-center gap-2">
              <Flame className="w-5 h-5" style={{ color: "#FF0055" }} /> Trending Now
            </h2>
            <Link to="/market" className="text-xs tracking-widest uppercase text-white/60 hover:text-white inline-flex items-center gap-1" data-testid="see-all-market">
              Market <ArrowRight className="w-3 h-3" />
            </Link>
          </div>
          <div className="space-y-3">
            {hot.map((idx) => (
              <Link key={idx.index_id} to={`/market/${idx.index_id}`} className="block" data-testid={`dash-trend-${idx.handle}`}>
                <TrendCard index={idx} />
              </Link>
            ))}
          </div>
        </section>

        <section>
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-display text-xl font-bold flex items-center gap-2">
              <TrendingUp className="w-5 h-5" style={{ color: "#00FF66" }} /> Pulse
            </h2>
          </div>
          <div className="space-y-3">
            {feed.slice(0, 6).map((p) => (
              <div key={p.post_id} className="glass rounded-2xl p-4 flex gap-3 card-hover">
                <Avatar name={p.index_name || p.title} category={p.index_category || "default"} symbol={p.index_symbol} size={48} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="chip" style={{ background: p.type === "hot" ? "#FF005522" : p.type === "event" ? "#00F0FF22" : "#FFD60022", color: p.type === "hot" ? "#FF0055" : p.type === "event" ? "#00F0FF" : "#FFD600" }}>{p.type}</span>
                    <span className="text-[10px] text-white/40 tracking-widest uppercase">{p.author}</span>
                  </div>
                  <p className="font-display text-base font-semibold mt-1 truncate">{p.title}</p>
                  <p className="text-sm text-white/60 line-clamp-2">{p.content}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        <p className="text-[10px] text-center text-white/40 leading-relaxed pt-2">
          Fanpuff is a free-to-play entertainment fantasy and social experience. Fanpuff Credits have no cash value and cannot be withdrawn. No deposits, withdrawals, or real-money gaming are involved. Rewards are promotional, milestone-based, and subject to eligibility, availability, and platform rules.
        </p>
        <div className="h-6" />
      </div>
      <BottomNav />
      <QuickTour open={tourOpen} onClose={() => setTourOpen(false)} />
    </div>
  );
}

function QuickTile({ icon: Icon, color, label, to, tid, onClick }) {
  return (
    <Link to={to} onClick={onClick} data-testid={tid}
      className="glass rounded-2xl p-2.5 flex flex-col items-center justify-center gap-1.5 card-hover text-center">
      <span className="w-9 h-9 rounded-xl grid place-items-center" style={{ background: `${color}22`, border: `1px solid ${color}55` }}>
        <Icon className="w-4 h-4" style={{ color }} />
      </span>
      <span className="text-[11px] font-semibold">{label}</span>
    </Link>
  );
}
